<script setup lang="ts">
import { computed, nextTick, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAddDonationModal } from '@/composables/useAddDonationModal'
import { useManualGivingDonations } from '@/composables/useManualGivingDonations'
import { useRecurringDonations } from '@/composables/useRecurringDonations'
import { useDashboardToast } from '@/composables/useDashboardToast'
import { parseAmountToPence } from '@/helpers/parseAmountToPence'
import { dateFormat } from '@/components/helpers/formats'
import debug from '@/helpers/debug'
import { ukTodayIso } from '@/helpers/recurring/dates'
import { recurringDonationsEnabled } from '@/helpers/recurringDonationsEnabled'
import { previewRecurringBackfill } from '@/helpers/recurring/generation'
import {
  defaultIsoDateForUkTaxYear,
  earliestRecordableDonationIsoDate,
  isRecordableDonationDate,
  ukTaxYearIsoDateBounds,
} from '@/helpers/givingRecord'
import {
  DONATION_ANALYTICS_EVENTS,
  trackDonationAnalytics,
} from '@/helpers/donationsAnalytics'
import type {
  RecurringBackfillPreview,
  RecurringEndCondition,
  RecurringEndConditionType,
  RecurringFrequency,
} from '@/types/recurringDonation'
import DonationGiftAidChoice from '@/components/dashboard/donations/DonationGiftAidChoice.vue'
import DashboardDateInput from '@/components/dashboard/ui/DashboardDateInput.vue'
import DonationKindChoice, {
  type DonationKind,
} from '@/components/dashboard/donations/DonationKindChoice.vue'
import RecurringScheduleFields from '@/components/dashboard/donations/RecurringScheduleFields.vue'
import DashboardFormField from '@/components/dashboard/ui/DashboardFormField.vue'
import DashboardModal from '@/components/dashboard/ui/DashboardModal.vue'

const { t } = useI18n()
const { isOpen, suggestedPeriod, closeAddDonationModal } = useAddDonationModal()
const { addManualDonation } = useManualGivingDonations()
const { addRecurringDonation } = useRecurringDonations()
const { showToast } = useDashboardToast()

const kind = ref<DonationKind | null>(null)
const charity = ref('')
const donationDate = ref('')
const amount = ref('')
const notes = ref('')
const giftAidDeclared = ref<boolean | null>(null)
const frequency = ref<RecurringFrequency>('monthly')
const endType = ref<RecurringEndConditionType>('until_stopped')
const endDate = ref('')
const occurrenceCount = ref('')
const isSubmitting = ref(false)
const schedulePreview = ref<RecurringBackfillPreview | null>(null)
const confirmingLargeBackfill = ref(false)
const largeBackfillPreview = ref<RecurringBackfillPreview | null>(null)
const limitPreview = ref<RecurringBackfillPreview | null>(null)

const fieldErrors = reactive({
  kind: '',
  charity: '',
  donationDate: '',
  amount: '',
  giftAid: '',
  endDate: '',
  occurrenceCount: '',
})

const clearFieldError = (field: keyof typeof fieldErrors) => {
  fieldErrors[field] = ''
}

const resetForm = () => {
  kind.value = null
  charity.value = ''
  donationDate.value = ''
  amount.value = ''
  notes.value = ''
  giftAidDeclared.value = null
  frequency.value = 'monthly'
  endType.value = 'until_stopped'
  endDate.value = ''
  occurrenceCount.value = ''
  isSubmitting.value = false
  schedulePreview.value = null
  confirmingLargeBackfill.value = false
  largeBackfillPreview.value = null
  limitPreview.value = null
  fieldErrors.kind = ''
  fieldErrors.charity = ''
  fieldErrors.donationDate = ''
  fieldErrors.amount = ''
  fieldErrors.giftAid = ''
  fieldErrors.endDate = ''
  fieldErrors.occurrenceCount = ''
}

const recurringEnabled = recurringDonationsEnabled()

watch(isOpen, (open) => {
  if (!open) {
    resetForm()
    return
  }
  // While the production switch is off, recording stays one-off.
  if (!recurringEnabled) kind.value = 'single'
  // Deep link from Claim tax back (or a year already selected on the giving
  // record) should open on a date in that tax year, not today, so the new
  // gift appears in the filtered list. A year wholly before the cut-off has
  // no day that can be saved, so leave the date empty.
  if (suggestedPeriod.value) {
    const suggested = defaultIsoDateForUkTaxYear(suggestedPeriod.value)
    if (isRecordableDonationDate(suggested)) donationDate.value = suggested
  }
})

const earliestDonationDate = computed(() => earliestRecordableDonationIsoDate())

/**
 * The date picker cannot offer a day before the 6 tax year cutoff.
 * A selected tax year still narrows the range, unless that whole year is
 * already too old: then the year max is dropped so the donor can move the
 * date forward without re-entering the rest of the form.
 */
const dateBounds = computed(() => {
  const earliest = earliestDonationDate.value
  const year =
    kind.value === 'single' && suggestedPeriod.value
      ? ukTaxYearIsoDateBounds(suggestedPeriod.value)
      : null
  if (!year || year.max < earliest) return { min: earliest, max: undefined }
  return {
    min: year.min > earliest ? year.min : earliest,
    max: year.max,
  }
})

const dateIsTooOld = computed(
  () => Boolean(donationDate.value) && !isRecordableDonationDate(donationDate.value)
)

watch(kind, () => {
  clearFieldError('kind')
  confirmingLargeBackfill.value = false
  largeBackfillPreview.value = null
  limitPreview.value = null
})
watch(charity, () => clearFieldError('charity'))
watch(donationDate, () => clearFieldError('donationDate'))
watch(amount, () => clearFieldError('amount'))
watch(giftAidDeclared, () => clearFieldError('giftAid'))
watch(endDate, () => clearFieldError('endDate'))
watch(occurrenceCount, () => clearFieldError('occurrenceCount'))
watch(endType, () => {
  clearFieldError('endDate')
  clearFieldError('occurrenceCount')
})

const tryEndCondition = (): RecurringEndCondition | null => {
  if (endType.value === 'until_stopped') return { type: 'until_stopped' }
  if (endType.value === 'on_date') {
    if (!endDate.value) return null
    return { type: 'on_date', endDate: endDate.value }
  }
  const count = Number.parseInt(occurrenceCount.value, 10)
  if (!Number.isInteger(count) || count < 1) return null
  return { type: 'after_occurrences', occurrenceCount: count }
}

const refreshSchedulePreview = () => {
  if (kind.value !== 'recurring' || !donationDate.value) {
    schedulePreview.value = null
    return
  }
  const endCondition = tryEndCondition()
  if (!endCondition) {
    schedulePreview.value = null
    return
  }
  schedulePreview.value = previewRecurringBackfill({
    firstDonationDate: donationDate.value,
    frequency: frequency.value,
    endCondition,
    todayIso: ukTodayIso(),
  })
}

watch(
  [kind, donationDate, frequency, endType, endDate, occurrenceCount],
  () => {
    // A 422 hides Save until this notice clears. A new schedule is a new check.
    limitPreview.value = null
    refreshSchedulePreview()
  }
)

const focusField = async (fieldId: string) => {
  await nextTick()
  document.getElementById(fieldId)?.focus()
}

const buildEndCondition = (): RecurringEndCondition | null => {
  if (endType.value === 'until_stopped') return { type: 'until_stopped' }
  if (endType.value === 'on_date') {
    if (!endDate.value) {
      fieldErrors.endDate = t('Dashboard.Giving.AddDonationModal.Toast.MissingEndDate')
      return null
    }
    if (donationDate.value && endDate.value < donationDate.value) {
      fieldErrors.endDate = t('Dashboard.Giving.AddDonationModal.Toast.InvalidEndDate')
      return null
    }
    return { type: 'on_date', endDate: endDate.value }
  }
  const count = Number.parseInt(occurrenceCount.value, 10)
  if (!Number.isInteger(count) || count < 1) {
    fieldErrors.occurrenceCount = t(
      'Dashboard.Giving.AddDonationModal.Toast.MissingOccurrenceCount'
    )
    return null
  }
  return { type: 'after_occurrences', occurrenceCount: count }
}

const validateSharedFields = async (): Promise<{
  charity: string
  amountPence: number
} | null> => {
  fieldErrors.kind = ''
  fieldErrors.charity = ''
  fieldErrors.donationDate = ''
  fieldErrors.amount = ''
  fieldErrors.giftAid = ''
  fieldErrors.endDate = ''
  fieldErrors.occurrenceCount = ''

  if (!kind.value) {
    fieldErrors.kind = t('Dashboard.Giving.AddDonationModal.KindMissing')
    await focusField('donation-kind-single')
    return null
  }
  const trimmedCharity = charity.value.trim()
  if (!trimmedCharity) {
    fieldErrors.charity = t('Dashboard.Giving.AddDonationModal.Toast.MissingCharity')
    await focusField('don-charity')
    return null
  }
  if (!donationDate.value) {
    fieldErrors.donationDate =
      kind.value === 'recurring'
        ? t('Dashboard.Giving.AddDonationModal.Toast.MissingFirstDate')
        : t('Dashboard.Giving.AddDonationModal.Toast.MissingDate')
    await focusField('don-date')
    return null
  }
  if (!isRecordableDonationDate(donationDate.value)) {
    debug('Rejected donation date before the 6 tax year cutoff', donationDate.value)
    fieldErrors.donationDate = t('Dashboard.Giving.AddDonationModal.Toast.DateTooOld', {
      date: dateFormat(earliestRecordableDonationIsoDate()) ?? '',
    })
    await focusField('don-date')
    return null
  }
  const amountPence = parseAmountToPence(amount.value)
  if (amountPence === null) {
    fieldErrors.amount = t('Dashboard.Giving.AddDonationModal.Toast.MissingAmount')
    await focusField('don-amount')
    return null
  }
  if (giftAidDeclared.value === null) {
    fieldErrors.giftAid = t('Dashboard.Giving.AddDonationModal.Toast.MissingGiftAid')
    await focusField('donation-gift-aid-yes')
    return null
  }
  return { charity: trimmedCharity, amountPence }
}

const saveSingleDonation = async (charityName: string, amountPence: number) => {
  await addManualDonation({
    charity: charityName,
    date: donationDate.value,
    amountPence,
    giftAidDeclared: giftAidDeclared.value === true,
    notes: notes.value.trim() || undefined,
  })
  showToast(t('Dashboard.Toast.DonationSaved'))
  resetForm()
  closeAddDonationModal()
}

const saveRecurringDonation = async (
  charityName: string,
  amountPence: number,
  confirmLargeBackfill = false
) => {
  const endCondition = buildEndCondition()
  if (!endCondition) {
    await focusField(
      endType.value === 'on_date' ? 'recurring-end-date' : 'recurring-occurrence-count'
    )
    return
  }

  const result = await addRecurringDonation({
    charity: charityName,
    amountPence,
    giftAidDeclared: giftAidDeclared.value === true,
    notes: notes.value.trim() || undefined,
    frequency: frequency.value,
    firstDonationDate: donationDate.value,
    endCondition,
    confirmLargeBackfill,
  })

  if ('limitExceeded' in result) {
    limitPreview.value = result.limitExceeded
    confirmingLargeBackfill.value = false
    largeBackfillPreview.value = null
    return
  }
  if ('confirmation' in result) {
    largeBackfillPreview.value = result.confirmation
    confirmingLargeBackfill.value = true
    limitPreview.value = null
    return
  }

  trackDonationAnalytics(DONATION_ANALYTICS_EVENTS.RecurringCreated, {
    frequency: frequency.value,
    end_condition: endCondition.type,
    occurrence_count: largeBackfillPreview.value?.dueCount ?? schedulePreview.value?.dueCount,
  })

  showToast(t('Dashboard.Giving.AddDonationModal.Toast.RecurringSaved'))
  resetForm()
  closeAddDonationModal()
}

const saveDonation = async (confirmLargeBackfill = false) => {
  if (isSubmitting.value) return

  const shared = await validateSharedFields()
  if (!shared || giftAidDeclared.value === null) return

  isSubmitting.value = true
  try {
    if (kind.value === 'recurring') {
      await saveRecurringDonation(
        shared.charity,
        shared.amountPence,
        confirmLargeBackfill
      )
      return
    }
    await saveSingleDonation(shared.charity, shared.amountPence)
  } catch (error) {
    console.error('Failed to save donation', error)
    showToast(t('Dashboard.Giving.AddDonationModal.Toast.SaveFailed'))
  } finally {
    isSubmitting.value = false
  }
}

const confirmBackfill = async () => {
  await saveDonation(true)
}

const cancelBackfillConfirm = () => {
  confirmingLargeBackfill.value = false
}

const showLimitNotice = computed(
  () =>
    !dateIsTooOld.value &&
    Boolean(limitPreview.value || schedulePreview.value?.exceedsLimit)
)

const pastDonationHint = computed(() => {
  const preview = schedulePreview.value
  if (dateIsTooOld.value) return ''
  if (!preview || preview.dueCount < 1 || preview.exceedsLimit) return ''
  if (preview.dueCount === 1) {
    return t('Dashboard.Giving.AddDonationModal.PastDonationsOne')
  }
  return t('Dashboard.Giving.AddDonationModal.PastDonationsMany', {
    count: preview.dueCount,
  })
})

const pastDonationRange = computed(() => {
  const preview = schedulePreview.value
  if (!preview?.fromDate || !preview.toDate) return ''
  return t('Dashboard.Giving.AddDonationModal.PastDonationsRange', {
    from: dateFormat(preview.fromDate),
    to: dateFormat(preview.toDate),
  })
})

const largeBackfillYearCount = computed(
  () => largeBackfillPreview.value?.taxYears.length ?? 0
)
</script>

<template>
  <DashboardModal
    :is-open="isOpen"
    aria-labelledby="add-donation-modal-title"
    :close-disabled="isSubmitting"
    @close="closeAddDonationModal"
  >
    <template v-if="confirmingLargeBackfill && largeBackfillPreview">
      <h2 id="add-donation-modal-title">
        {{
          $t('Dashboard.Giving.AddDonationModal.BackfillConfirmTitle', {
            count: largeBackfillPreview.dueCount,
          })
        }}
      </h2>
      <p>{{ $t('Dashboard.Giving.AddDonationModal.BackfillConfirmBody') }}</p>
      <p v-if="largeBackfillYearCount > 1">
        {{
          $t('Dashboard.Giving.AddDonationModal.BackfillConfirmYears', {
            count: largeBackfillYearCount,
          })
        }}
      </p>
    </template>

    <template v-else>
      <div class="sa-modal-icon sa-modal-icon--blue" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 5v14M5 12h14" />
        </svg>
      </div>
      <h2 id="add-donation-modal-title">{{ $t('Dashboard.Giving.AddDonationModal.Title') }}</h2>
      <p v-if="!kind">{{ $t('Dashboard.Giving.AddDonationModal.Intro') }}</p>

      <DonationKindChoice
        v-if="recurringEnabled"
        v-model="kind"
        :compact="Boolean(kind)"
        :error="fieldErrors.kind"
      />

      <template v-if="kind">
      <!-- Section: what was given. Fields in one group sit a field gap apart. -->
      <div class="sa-form-section">
        <DashboardFormField
          :label="$t('Dashboard.Giving.AddDonationModal.CharityLabel')"
          label-for="don-charity"
          :error="fieldErrors.charity"
        >
        <template #default="{ errorId, hasError }">
          <input
            id="don-charity"
            v-model="charity"
            class="sa-input"
            type="text"
            name="charity"
            autocomplete="organization"
            enterkeyhint="next"
            :placeholder="$t('Dashboard.Giving.AddDonationModal.CharityPlaceholder')"
            :aria-invalid="hasError || undefined"
            :aria-describedby="hasError ? errorId : undefined"
          />
        </template>
      </DashboardFormField>

      <div class="sa-form-row">
        <DashboardFormField
          :label="
            kind === 'recurring'
              ? $t('Dashboard.Giving.AddDonationModal.FirstDateLabel')
              : $t('Dashboard.Giving.AddDonationModal.DateLabel')
          "
          label-for="don-date"
          :error="fieldErrors.donationDate"
        >
          <template #default="{ errorId, hasError }">
            <DashboardDateInput
              id="don-date"
              v-model="donationDate"
              name="donation-date"
              enterkeyhint="next"
              :min="dateBounds.min"
              :max="dateBounds.max"
              :aria-invalid="hasError || undefined"
              :aria-describedby="hasError ? errorId : undefined"
            />
          </template>
        </DashboardFormField>

        <DashboardFormField
          :label="$t('Dashboard.Giving.AddDonationModal.AmountLabel')"
          label-for="don-amount"
          :error="fieldErrors.amount"
        >
          <template #default="{ errorId, hasError }">
            <input
              id="don-amount"
              v-model="amount"
              class="sa-input"
              type="text"
              name="amount"
              inputmode="decimal"
              autocomplete="off"
              enterkeyhint="next"
              :placeholder="$t('Dashboard.Giving.AddDonationModal.AmountPlaceholder')"
              :aria-invalid="hasError || undefined"
              :aria-describedby="hasError ? errorId : undefined"
            />
          </template>
        </DashboardFormField>
      </div>

      <!--
        Schedule fields describe the same donation as date and amount, so they
        stay in this group at a field gap. How many past donations will be
        added is shown above the save button.
      -->
      <RecurringScheduleFields
        v-if="kind === 'recurring'"
        v-model:frequency="frequency"
        v-model:end-type="endType"
        v-model:end-date="endDate"
        v-model:occurrence-count="occurrenceCount"
        :end-date-error="fieldErrors.endDate"
        :occurrence-count-error="fieldErrors.occurrenceCount"
      />
      </div>

      <!-- Section: Gift Aid. -->
      <div class="sa-form-section">
        <DonationGiftAidChoice
          v-model="giftAidDeclared"
          :context="kind === 'recurring' ? 'recurring' : 'single'"
          input-name="donation-gift-aid-declared"
          :error="fieldErrors.giftAid"
        />
      </div>

      <!-- Section: notes. -->
      <div class="sa-form-section">
        <DashboardFormField
          :label="$t('Dashboard.Giving.AddDonationModal.NotesLabel')"
          label-for="don-notes"
        >
          <template #default>
            <input
              id="don-notes"
              v-model="notes"
              class="sa-input"
              type="text"
              name="notes"
              enterkeyhint="done"
              :placeholder="$t('Dashboard.Giving.AddDonationModal.NotesPlaceholder')"
            />
          </template>
        </DashboardFormField>
      </div>
    </template>
    </template>

    <template #footer>
      <p
        v-if="showLimitNotice && !confirmingLargeBackfill"
        class="sa-recurring-backfill-hint"
        role="alert"
      >
        <strong>{{ $t('Dashboard.Giving.AddDonationModal.BackfillLimitTitle') }}</strong>
        <span>
          {{
            $t('Dashboard.Giving.AddDonationModal.BackfillLimitBody', {
              limit: (limitPreview ?? schedulePreview)?.limit,
            })
          }}
        </span>
      </p>
      <p
        v-else-if="pastDonationHint && !confirmingLargeBackfill"
        class="sa-recurring-backfill-hint"
      >
        <strong>{{ pastDonationHint }}</strong>
        <span v-if="pastDonationRange">{{ pastDonationRange }}</span>
      </p>
      <div class="sa-modal-actions">
        <button
          type="button"
          class="sa-btn-secondary"
          :disabled="isSubmitting"
          @click="confirmingLargeBackfill ? cancelBackfillConfirm() : closeAddDonationModal()"
        >
          {{
            confirmingLargeBackfill
              ? $t('Dashboard.Giving.AddDonationModal.BackfillGoBack')
              : $t('Buttons.Cancel')
          }}
        </button>
        <button
          v-if="confirmingLargeBackfill"
          type="button"
          class="sa-btn-primary sa-modal-actions__primary"
          :disabled="isSubmitting"
          :aria-busy="isSubmitting"
          @click="confirmBackfill"
        >
          <span v-if="isSubmitting" class="spinner sa-btn-spinner" aria-hidden="true" />
          <span>{{
            isSubmitting
              ? $t('Accessibility.Button.Loading')
              : $t('Dashboard.Giving.AddDonationModal.BackfillConfirmCta')
          }}</span>
        </button>
        <button
          v-else-if="kind && !showLimitNotice"
          type="button"
          class="sa-btn-primary sa-modal-actions__primary"
          :disabled="isSubmitting"
          :aria-busy="isSubmitting"
          @click="saveDonation(false)"
        >
          <span v-if="isSubmitting" class="spinner sa-btn-spinner" aria-hidden="true" />
          <span>{{
            isSubmitting
              ? $t('Accessibility.Button.Loading')
              : kind === 'recurring'
                ? $t('Dashboard.Giving.AddDonationModal.SaveRecurring')
                : $t('Dashboard.Giving.AddDonationModal.Save')
          }}</span>
        </button>
      </div>
    </template>
  </DashboardModal>
</template>
